<template>
	<c-section ref="section" class="cog-rating-scale" :class="{'cog-table-bands': !chameleon, ['cog-field--' + fieldIndex]: fieldIndex}" :cols="cols" :source="$source" :hide-label="hideLabel">
		<table v-if="$source.readonly">
			<tbody>
				<template v-for="question in questions">
					<vm-source :key="question" v-slot="{ $source: field }" :source="question">
						<tr>
							<th class="cog-rating-scale__question">
								<label>
									<slot name="readonly-question-label" :item="field">{{ field.label }}</slot>
									<span v-if="showAsterisk(field)" class="cog-asterisk">*</span>
									<span v-if="isDirty && lostFocus && field.firstError" class="cog-error-message cog-error-message--inline">{{ $resource("required-asterisk") }}</span>
								</label>
							</th>
							<td><slot name="readonly-question-value" :item="field">{{ field.displayValue }}</slot></td>
						</tr>
					</vm-source>
				</template>
			</tbody>
		</table>
		<table v-else-if="!condensed">
			<vm-source v-slot="{ $source }" :source="questions[0]">
				<thead>
					<th></th>
					<th v-for="(rating, i) in ratings" :id="`cog-${$source.parent.id}-${i}`" :key="rating.value"><span>{{ rating.value }}</span></th>
				</thead>
			</vm-source>
			<tbody>
				<template v-for="question in questions">
					<vm-source :key="question" v-slot="{ $source: field }" :source="question">
						<tr is="el-radio-group" v-model="field.value" :chameleon="chameleon" :class="{'is-required': field.required}">
							<th class="cog-rating-scale__question">
								<label :id="'cog-'+field.id">{{ field.label }}<span v-if="showAsterisk(field)" aria-hidden="true" class="cog-asterisk">*</span>
									<span v-if="field.required && !(isDirty && lostFocus && field.firstError)" class="cog-offscreen">({{ $resource("required-asterisk") }})</span>
									<span v-if="isDirty && lostFocus && field.firstError" class="cog-offscreen">({{ field.firstError }})</span>
								</label>
								<span v-if="isDirty && lostFocus && field.firstError" class="cog-error-message cog-error-message--inline">{{ $resource("required-asterisk") }}</span>
							</th>
							<td v-for="(rating, i) in ratings" :key="rating.value" class="cog-rating-scale__option" aria-hidden="true">
								<el-radio v-model="field.value" :chameleon="chameleon" :label="rating.value" :aria-labelledby="`cog-${field.id} cog-${field.parent.id}-${i}`"> </el-radio>
							</td>
						</tr>
					</vm-source>
				</template>
			</tbody>
		</table>
		<template v-for="(question, i) in questions" v-else>
			<c-row :key="question">
				<c-field v-slot="{ field }" :field-index="i" :source="question">
					<c-select :id="field.id" v-model="field.displayValue" :scope="scope" :options="ratings" :chameleon="chameleon" :mobile="mobile" />
				</c-field>
			</c-row>
		</template>
	</c-section>
</template>

<script>
	import '../style/_table.scss';
	import '../style/_fancy-checkables.scss';

	import Vue from 'vue';
	import { RadioGroup, Radio } from '@cognitoforms/element-ui';
	import CSection from './Section.vue';
	import CSelect from './Select.vue';
	import Responsive from '../mixins/responsive';
	import ValidatedSourcePath from '../mixins/validated-source-path';
	import Col from './Col.vue';
	import { FormEvents } from 'src/framework/public/events';

	Vue.use(RadioGroup);
	Vue.use(Radio);

	export default {
		name: 'CRatingScale',
		components: {
			'el-radio': Radio,
			'el-radio-group': RadioGroup,
			CSection,
			CSelect
		},
		mixins: [ValidatedSourcePath, Responsive, Col],
		inject: ['$resource', 'formEvents'],
		props: {
			hideLabel: Boolean,
			chameleon: Boolean,
			mobile: Boolean,
			scope: { type: String, default: '0' },
			fieldIndex: { type: String, default: null }

		},
		data() {
			return {
				conditionCount: 0,
				conditionListener: null
			};
		},
		computed: {
			questions() {
				return this.$source.property.propertyType.meta.properties.filter(p => !p.isCalculated && p.name !== 'Form' && p.name !== 'ParentSection' && p.name !== 'Id').map(p => p.name);
			},
			ratings() {
				return this.$source.lastTarget.get(this.$source.property.name + '_Choices').map(c => { return { value: c.Label, displayValue: c.Label }; });
			},
			hasError() {
				return this.conditionCount > 0;
			}
		},
		watch: {
			'$source.value'(newValue, oldValue) {
				if (newValue !== oldValue) {
					if (oldValue)
						this.removeSubscriber(oldValue);
					if (newValue)
						this.initializeSubscriber(newValue);
				}
			}
		},
		created() {
			if (this.formEvents)
				this.formEvents.on(FormEvents.ResetEntry, this.refreshRatingScale);

			if (this.$source.value)
				this.initializeSubscriber(this.$source.value);
		},
		mounted () {
			this.$refs.section.lostFocus = true;
		},
		beforeDestroy() {
			if (this.$source.value)
				this.removeSubscriber(this.$source.value);
		},
		methods: {
			showAsterisk(field) {
				return field.required && !this.form.isReadonly;
			},
			initializeSubscriber(entity) {
				if (entity.meta?.conditions) {
					// eslint-disable-next-line @typescript-eslint/no-this-alias
					const vm = this;
					vm.conditionCount = entity.meta.conditions.length;
					this.conditionListener = function(e) {
						// `this` is the condition list
						vm.conditionCount = this.length;
					};
					entity.meta.conditions.changed.subscribe(this.conditionListener);
				}
			},
			removeSubscriber(entity) {
				if (entity?.meta?.conditions)
					entity.meta.conditions.changed.unsubscribe(this.conditionListener);
			},
			refreshRatingScale() {
				this.isDirty = false;
				this.lostFocus = false;
				if (this.$source.value) {
					this.detectDirtyEntity();
					this.removeSubscriber(this.$source.value);
					this.initializeSubscriber(this.$source.value);
				}
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-rating-scale {
		// The reset ensures this is normal except for in Chameleon. Cf. 20403
		word-break: normal;

		table {

			thead th + th {
				width: auto;
			}

			thead th {
				font-weight: bold;
				text-align: center;
			}

			th {
				padding: $gutter-third;
			}

			// Zero horizontal padding because radio buttons are doubled in size and scaled down x2.
			// This is done to smooth out oddities in checkable shape, but takes up a lot of room in this context.
			// No padding compensates for this extra space.
			td {
				padding: $gutter-third 0;
			}
		}

		&__question label:not(:last-child) {
			margin-right: .5em;
		}

		&__question {
			font-weight: normal;
			text-align: left;
		}

		&__question.cog-rating-scale__question {
			vertical-align: middle;
		}

		&__option {
			text-align: center;
		}

		&__option .el-radio__label {
			display: none !important;
		}
	}
}
</style>
